import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Users, Waves, Calendar, Star, RotateCcw, Trash2, TrendingUp, Euro, ShieldAlert, MessageSquare, Send } from "lucide-react";

export default function AdminDashboard() {
  const { t, user, loading } = useApp();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pools, setPools] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [settings, setSettings] = useState({ iban: "", iban_holder: "", bic: "", kill_switch_active: false });
  const [savingSettings, setSavingSettings] = useState(false);
  const [replyTo, setReplyTo] = useState({});

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    if (user.role !== "admin") { nav("/"); return; }
    reload();
  }, [user, loading]); // eslint-disable-line

  const reload = async () => {
    const [s, u, p, b, r, tk, st] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/pools"),
      api.get("/admin/bookings"), api.get("/admin/reviews"), api.get("/admin/tickets"),
      api.get("/admin/settings"),
    ]);
    setStats(s.data); setUsers(u.data); setPools(p.data); setBookings(b.data); setReviews(r.data); setTickets(tk.data); setSettings(st.data);
  };

  const refund = async (id) => {
    if (!window.confirm(t("admin.confirmRefund"))) return;
    try { await api.post(`/admin/bookings/${id}/refund`); toast.success(t("admin.refunded")); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
  };
  const delPool = async (id) => { if (!window.confirm(t("admin.confirmDelete"))) return; await api.delete(`/admin/pools/${id}`); toast.success("Deleted"); reload(); };
  const delReview = async (id) => { if (!window.confirm(t("admin.confirmDelete"))) return; await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); reload(); };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.patch("/admin/settings", {
        iban: settings.iban, iban_holder: settings.iban_holder, bic: settings.bic,
      });
      setSettings(data);
      toast.success(t("admin.saved"));
    } catch (e) { toast.error("Failed"); } finally { setSavingSettings(false); }
  };

  const toggleKillSwitch = async (val) => {
    if (val && !window.confirm("Confirm: block the site for all non-admin users NOW?")) return;
    try {
      const { data } = await api.patch("/admin/settings", { kill_switch_active: val });
      setSettings(data);
      toast[val ? "warning" : "success"](val ? t("admin.killOn") : t("admin.killOff"));
    } catch (e) { toast.error("Failed"); }
  };

  const sendReply = async (ticket) => {
    const reply = replyTo[ticket.ticket_id] || "";
    if (!reply.trim()) return;
    try {
      await api.patch(`/admin/tickets/${ticket.ticket_id}`, { status: "in_progress", admin_reply: reply });
      toast.success("Reply sent");
      setReplyTo({ ...replyTo, [ticket.ticket_id]: "" });
      reload();
    } catch (e) { toast.error("Failed"); }
  };
  const markResolved = async (ticket) => {
    await api.patch(`/admin/tickets/${ticket.ticket_id}`, { status: "resolved" });
    reload();
  };

  if (!user || user.role !== "admin") return null;
  const openTickets = tickets.filter((tk) => tk.status !== "resolved" && tk.status !== "closed").length;
  const statusColor = { open: "bg-red-100 text-red-700", in_progress: "bg-blue-100 text-blue-700", resolved: "bg-green-100 text-green-700", closed: "bg-muted text-muted-foreground" };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="overline text-[#E27B58] mb-2">{t("admin.badge")}</div>
          <h1 className="font-display text-4xl font-bold">{t("admin.title")}</h1>
        </div>
        {settings.kill_switch_active && (
          <div className="flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-semibold" data-testid="kill-switch-banner">
            <ShieldAlert className="w-4 h-4" /> {t("admin.killOn")}
          </div>
        )}
      </div>

      {stats && (
        <div className="grid md:grid-cols-5 gap-4 my-8">
          {[
            { icon: Users, label: t("admin.users"), val: stats.users },
            { icon: Waves, label: t("admin.pools"), val: stats.pools },
            { icon: Calendar, label: t("admin.bookings"), val: stats.bookings_paid },
            { icon: TrendingUp, label: t("admin.gross"), val: `€${stats.gross_volume.toFixed(2)}` },
            { icon: Euro, label: t("admin.platformRev"), val: `€${stats.platform_revenue.toFixed(2)}` },
          ].map((c, i) => (
            <div key={i} className="bg-white border border-border rounded-2xl p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2"><c.icon className="w-3.5 h-3.5" /> {c.label}</div>
              <div className="font-display text-2xl font-bold">{c.val}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="bookings">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="bookings" data-testid="tab-bookings">{t("admin.bookings")}</TabsTrigger>
          <TabsTrigger value="pools" data-testid="tab-pools">{t("admin.pools")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">{t("admin.users")}</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">{t("admin.reviews")}</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">
            {t("admin.tickets")} {openTickets > 0 && <span className="ml-1.5 bg-[#E27B58] text-white text-xs px-1.5 rounded-full">{openTickets}</span>}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">{t("admin.settings")}</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings">
          <div className="bg-white border border-border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr>
                <th className="text-left px-4 py-3">ID</th><th className="text-left px-4 py-3">Pool</th>
                <th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Action</th>
              </tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_id} className="border-t border-border" data-testid={`admin-booking-${b.booking_id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{b.booking_id.slice(-8)}</td>
                    <td className="px-4 py-3">{b.pool_title}</td>
                    <td className="px-4 py-3">{b.date}</td>
                    <td className="px-4 py-3">€{b.amount?.toFixed(2)}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${b.payment_status === "paid" ? "bg-green-100 text-green-700" : b.payment_status === "refunded" ? "bg-orange-100 text-orange-700" : "bg-muted"}`}>{b.payment_status}</span></td>
                    <td className="px-4 py-3 text-right">{b.payment_status === "paid" && <Button size="sm" variant="outline" onClick={() => refund(b.booking_id)} data-testid={`refund-${b.booking_id}`}><RotateCcw className="w-3 h-3 mr-1" /> {t("admin.refund")}</Button>}</td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">No bookings</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="pools">
          <div className="bg-white border border-border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Hotel</th><th className="text-left px-4 py-3">City</th><th className="text-left px-4 py-3">Price</th><th className="text-left px-4 py-3">Rating</th><th className="text-right px-4 py-3">Action</th></tr></thead>
              <tbody>
                {pools.map((p) => (
                  <tr key={p.pool_id} className="border-t border-border">
                    <td className="px-4 py-3">{p.title}</td><td className="px-4 py-3">{p.hotel_name}</td>
                    <td className="px-4 py-3">{p.city}</td><td className="px-4 py-3">€{p.price_per_day}</td>
                    <td className="px-4 py-3">{p.rating_avg} ({p.review_count})</td>
                    <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => delPool(p.pool_id)} className="text-destructive" data-testid={`admin-delpool-${p.pool_id}`}><Trash2 className="w-3 h-3" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="bg-white border border-border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Provider</th><th className="text-left px-4 py-3">Joined</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-border">
                    <td className="px-4 py-3">{u.name}</td><td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-[#005C7A]/10 text-[#005C7A]">{u.role}</span></td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{u.provider}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{u.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          <div className="space-y-3">
            {reviews.map((r) => (
              <div key={r.review_id} className="bg-white border border-border rounded-2xl p-5 flex items-start gap-4">
                <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-[#E27B58] text-[#E27B58]" : "text-muted-foreground/30"}`} />)}</div>
                <div className="flex-1"><div className="text-sm font-semibold">{r.guest_name}</div><div className="text-sm text-muted-foreground">{r.comment}</div></div>
                <Button size="sm" variant="outline" onClick={() => delReview(r.review_id)} className="text-destructive" data-testid={`admin-delreview-${r.review_id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
            {reviews.length === 0 && <div className="text-center py-8 text-muted-foreground">No reviews</div>}
          </div>
        </TabsContent>

        <TabsContent value="tickets">
          <div className="space-y-4">
            {tickets.map((tk) => (
              <div key={tk.ticket_id} className="bg-white border border-border rounded-2xl p-5" data-testid={`ticket-${tk.ticket_id}`}>
                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[tk.status] || "bg-muted"}`}>{t(`admin.ticketStatus.${tk.status}`)}</span>
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">{tk.category}</span>
                    </div>
                    <div className="font-semibold">{tk.subject}</div>
                    <div className="text-xs text-muted-foreground">{tk.name} · {tk.email} · {tk.created_at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground bg-muted/40 rounded-lg p-3 mb-3 whitespace-pre-line">{tk.message}</div>
                {tk.admin_reply && (
                  <div className="text-sm bg-[#005C7A]/5 border-l-2 border-[#005C7A] rounded-r p-3 mb-3">
                    <div className="text-xs font-semibold text-[#005C7A] mb-1">Admin reply</div>
                    <div className="text-muted-foreground whitespace-pre-line">{tk.admin_reply}</div>
                  </div>
                )}
                {tk.status !== "resolved" && tk.status !== "closed" && (
                  <div className="flex flex-col gap-2">
                    <Textarea rows={2} placeholder={t("admin.ticketReplyPlaceholder")} value={replyTo[tk.ticket_id] || ""} onChange={(e) => setReplyTo({ ...replyTo, [tk.ticket_id]: e.target.value })} data-testid={`ticket-reply-${tk.ticket_id}`} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => sendReply(tk)} className="rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid={`send-reply-${tk.ticket_id}`}><Send className="w-3 h-3 mr-1" /> {t("admin.ticketSend")}</Button>
                      <Button size="sm" variant="outline" onClick={() => markResolved(tk)} className="rounded-full" data-testid={`resolve-${tk.ticket_id}`}>{t("admin.ticketMarkResolved")}</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {tickets.length === 0 && <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-2xl"><MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" /> No tickets yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-border rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-1"><Euro className="w-5 h-5 text-[#005C7A]" /><h2 className="font-display text-xl font-semibold">{t("admin.ibanTitle")}</h2></div>
              <p className="text-sm text-muted-foreground mb-5">{t("admin.ibanHelper")}</p>
              <div className="space-y-4">
                <div><Label>{t("admin.iban")}</Label><Input value={settings.iban || ""} onChange={(e) => setSettings({ ...settings, iban: e.target.value.toUpperCase() })} placeholder="IT60 X054 2811 1010 0000 0123 456" data-testid="input-iban" /></div>
                <div><Label>{t("admin.ibanHolder")}</Label><Input value={settings.iban_holder || ""} onChange={(e) => setSettings({ ...settings, iban_holder: e.target.value })} placeholder="Nome intestatario" data-testid="input-iban-holder" /></div>
                <div><Label>{t("admin.bic")}</Label><Input value={settings.bic || ""} onChange={(e) => setSettings({ ...settings, bic: e.target.value.toUpperCase() })} placeholder="BPPIITRRXXX" data-testid="input-bic" /></div>
                <Button onClick={saveSettings} disabled={savingSettings} className="rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid="save-iban">{savingSettings ? "..." : t("admin.save")}</Button>
              </div>
            </div>

            <div className="bg-white border border-border rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-1"><ShieldAlert className={`w-5 h-5 ${settings.kill_switch_active ? "text-red-600" : "text-[#005C7A]"}`} /><h2 className="font-display text-xl font-semibold">{t("admin.killTitle")}</h2></div>
              <p className="text-sm text-muted-foreground mb-5">{t("admin.killHelper")}</p>
              <div className={`p-5 rounded-xl border-2 ${settings.kill_switch_active ? "border-red-300 bg-red-50" : "border-green-200 bg-green-50/40"}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-semibold mb-1">{t("admin.killLabel")}</div>
                    <div className={`text-sm font-semibold ${settings.kill_switch_active ? "text-red-600" : "text-green-700"}`}>
                      {settings.kill_switch_active ? "🔒 " + t("admin.killOn") : "🟢 " + t("admin.killOff")}
                    </div>
                  </div>
                  <Switch checked={settings.kill_switch_active} onCheckedChange={toggleKillSwitch} data-testid="kill-switch-toggle" />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
